import { FC, useMemo, useState } from "react";
import styles from "./IRScanAnalysis.module.less";
import TypeComponentCard from "../typeComponentCard/TypeComponentCard";
import {
  ThermalLiDARResponseParameters,
  ThermalScanAnalysisParameters,
} from "~/pages/copilotMessage/types/copilot";
import { useGetProcessedThermalImages } from "./hooks/useGetProcessedThermalImages";
import { Popover, Spin, Switch, Typography } from "antd";
import ThermalImage from "./components/thermalImage/ThermalImage";
import { NoDataAvailable } from "~/components/noDataAvailable/NoDataAvailable";
import IRColorScale from "~/pages/copilotMessage/components/conversationDetail/components/conversationMessages/components/mikeMessage/components/irColorScale/IRColorScale";
import { getFullScreenChartSubTitle } from "../../helpers/chartHelper";
import {
  convertFahrenheitToUserPreference,
  getUnit,
} from "~/helpers/userPreference";
import { UNITS } from "~/constants/unit";
import { calculateColorMaxTempValue } from "./types/thermal";
import { VisualMapColorScale } from "~/components/visualMapColorScale/VisualMapColorScale";
import { useLanguage } from "~/hooks/useLanguage";

interface IRScanAnalysisProps {
  parameters: ThermalLiDARResponseParameters;
}

const IRScanAnalysis: FC<IRScanAnalysisProps> = ({ parameters }) => {
  const { formatMessage } = useLanguage();

  const [applyFactor, setApplyFactor] = useState({
    north: true,
    east: true,
    west: true,
  });
  const [westFullScreen, setWestFullScreen] = useState(false);
  const [eastFullScreen, setEastFullScreen] = useState(false);
  const [northFullScreen, setNorthFullScreen] = useState(false);

  const [northMaxTempValue, setNorthMaxTempValue] = useState<number>();
  const [eastMaxTempValue, setEastMaxTempValue] = useState<number>();
  const [westMaxTempValue, setWestMaxTempValue] = useState<number>();

  const thermalParams = useMemo(() => {
    return parameters.ProcessedHeatThermalData as ThermalScanAnalysisParameters;
  }, [parameters]);

  const { processedThermalImages, processedThermalImagesLoading } =
    useGetProcessedThermalImages(
      thermalParams.CampaignHeatId,
      thermalParams.SetId,
      thermalParams.Hotspots,
    );

  const northMaxTempValueMemo = useMemo(() => {
    return (
      processedThermalImages?.north?.concerningTemp?.temp ??
      northMaxTempValue ??
      0
    );
  }, [processedThermalImages, northMaxTempValue]);

  const eastMaxTempValueMemo = useMemo(() => {
    return (
      processedThermalImages?.east?.concerningTemp?.temp ??
      eastMaxTempValue ??
      0
    );
  }, [processedThermalImages, eastMaxTempValue]);

  const westMaxTempValueMemo = useMemo(() => {
    return (
      processedThermalImages?.west?.concerningTemp?.temp ??
      westMaxTempValue ??
      0
    );
  }, [processedThermalImages, westMaxTempValue]);

  const northMaxTempColor = useMemo(() => {
    return calculateColorMaxTempValue(northMaxTempValueMemo);
  }, [northMaxTempValueMemo]);

  const eastMaxTempColor = useMemo(() => {
    return calculateColorMaxTempValue(eastMaxTempValueMemo);
  }, [eastMaxTempValueMemo]);

  const westMaxTempColor = useMemo(() => {
    return calculateColorMaxTempValue(westMaxTempValueMemo);
  }, [westMaxTempValueMemo]);

  const CampaignInfoSubTitle = useMemo(() => {
    return (
      <div style={{ display: "flex", gap: "10px" }}>
        <span>{`Campaign: ${
          thermalParams?.CampaignName
        } ${getFullScreenChartSubTitle(
          thermalParams.HeatNumber,
          thermalParams.HeatName,
        )}`}</span>
      </div>
    );
  }, [thermalParams]);

  const thermalScale = useMemo(() => {
    return (
      <VisualMapColorScale
        colorScale={[
          {
            color: "#00fe00",
            min: 0,
            max: 700,
          },
          {
            color: "#ffff00",
            min: 700,
            max: 850,
          },
          {
            color: "#ff0100",
            min: 850,
          },
        ]}
        converter={convertFahrenheitToUserPreference}
        unit={UNITS.CELSIUS}
      />
    );
  }, []);

  return (
    <div className={styles.IRScanAnalysis}>
      <div className={styles.chartContainer}>
        <TypeComponentCard
          title={"West"}
          subTitle={westFullScreen ? CampaignInfoSubTitle : undefined}
          onShowFullScreen={setWestFullScreen}
          extraComponent={
            <div className={styles.maxValueWrapper}>
              {!!westMaxTempValueMemo && (
                <>
                  <Popover content={thermalScale}>
                    <span
                      className={`${styles.valueColor} ${
                        styles["color" + westMaxTempColor.color]
                      }`}
                    ></span>
                  </Popover>

                  <Typography.Title level={2}>
                    {processedThermalImages?.west?.concerningTemp
                      ? formatMessage("IRScanAnalysis.ConcerningMax")
                      : formatMessage("IRScanAnalysis.Max")}
                    {convertFahrenheitToUserPreference(
                      westMaxTempValueMemo *
                        (applyFactor.west
                          ? (processedThermalImages?.west?.thermalCalibration
                              ?.factor ?? 1)
                          : 1),
                    ).toFixed(0)}
                    {getUnit(UNITS.CELSIUS)}
                  </Typography.Title>
                </>
              )}

              {(processedThermalImages?.west?.thermalCalibration?.factor ?? 1) >
              1 ? (
                <span style={{ paddingLeft: 6 }}>
                  <Switch
                    checkedChildren="Corrected"
                    unCheckedChildren="Original"
                    checked={applyFactor.west}
                    onChange={(v) =>
                      setApplyFactor({ ...applyFactor, west: v })
                    }
                  />
                </span>
              ) : null}
            </div>
          }
        >
          {processedThermalImagesLoading ? (
            <Spin size="large" />
          ) : processedThermalImages?.west ? (
            <ThermalImage
              key={applyFactor.west ? "west.corrected" : "west.actual"}
              data={processedThermalImages?.west}
              applyFactor={applyFactor.west}
              concerningTemp={processedThermalImages?.west?.concerningTemp}
              onLoadMaxTempValue={setWestMaxTempValue}
            />
          ) : (
            <NoDataAvailable />
          )}
        </TypeComponentCard>

        <TypeComponentCard
          title={"East"}
          subTitle={eastFullScreen ? CampaignInfoSubTitle : undefined}
          onShowFullScreen={setEastFullScreen}
          extraComponent={
            <div className={styles.maxValueWrapper}>
              {!!eastMaxTempValueMemo && (
                <>
                  <Popover content={thermalScale}>
                    <span
                      className={`${styles.valueColor} ${
                        styles["color" + eastMaxTempColor.color]
                      }`}
                    ></span>
                  </Popover>

                  <Typography.Title level={2}>
                    {processedThermalImages?.east?.concerningTemp
                      ? formatMessage("IRScanAnalysis.ConcerningMax")
                      : formatMessage("IRScanAnalysis.Max")}
                    {convertFahrenheitToUserPreference(
                      eastMaxTempValueMemo *
                        (applyFactor.east
                          ? (processedThermalImages?.east?.thermalCalibration
                              ?.factor ?? 1)
                          : 1),
                    ).toFixed(0)}
                    {getUnit(UNITS.CELSIUS)}
                  </Typography.Title>
                </>
              )}

              {(processedThermalImages?.east?.thermalCalibration?.factor ?? 1) >
              1 ? (
                <span style={{ paddingLeft: 6 }}>
                  <Switch
                    checkedChildren="Corrected"
                    unCheckedChildren="Original"
                    checked={applyFactor.east}
                    onChange={(v) =>
                      setApplyFactor({ ...applyFactor, east: v })
                    }
                  />
                </span>
              ) : null}
            </div>
          }
        >
          {processedThermalImagesLoading ? (
            <Spin size="large" />
          ) : processedThermalImages?.east ? (
            <ThermalImage
              key={applyFactor.east ? "east.corrected" : "east.actual"}
              data={processedThermalImages?.east}
              applyFactor={applyFactor.east}
              concerningTemp={processedThermalImages?.east?.concerningTemp}
              onLoadMaxTempValue={setEastMaxTempValue}
            />
          ) : (
            <NoDataAvailable />
          )}
        </TypeComponentCard>

        <TypeComponentCard
          title={"North"}
          subTitle={northFullScreen ? CampaignInfoSubTitle : undefined}
          onShowFullScreen={setNorthFullScreen}
          extraComponent={
            <div className={styles.maxValueWrapper}>
              {!!northMaxTempValueMemo && (
                <>
                  <Popover content={thermalScale}>
                    <span
                      className={`${styles.valueColor} ${
                        styles["color" + northMaxTempColor.color]
                      }`}
                    ></span>
                  </Popover>

                  <Typography.Title level={2}>
                    {processedThermalImages?.north?.concerningTemp
                      ? formatMessage("IRScanAnalysis.ConcerningMax")
                      : formatMessage("IRScanAnalysis.Max")}
                    {convertFahrenheitToUserPreference(
                      northMaxTempValueMemo *
                        (applyFactor.north
                          ? (processedThermalImages?.north?.thermalCalibration
                              ?.factor ?? 1)
                          : 1),
                    ).toFixed(0)}
                    {getUnit(UNITS.CELSIUS)}
                  </Typography.Title>
                </>
              )}

              {(processedThermalImages?.north?.thermalCalibration?.factor ??
                1) > 1 ? (
                <span style={{ paddingLeft: 6 }}>
                  <Switch
                    checkedChildren="Corrected"
                    unCheckedChildren="Original"
                    checked={applyFactor.north}
                    onChange={(v) =>
                      setApplyFactor({ ...applyFactor, north: v })
                    }
                  />
                </span>
              ) : null}
            </div>
          }
        >
          {processedThermalImagesLoading ? (
            <Spin size="large" />
          ) : processedThermalImages?.north ? (
            <ThermalImage
              key={applyFactor.north ? "north.corrected" : "north.original"}
              data={processedThermalImages?.north}
              applyFactor={applyFactor.north}
              concerningTemp={processedThermalImages?.north?.concerningTemp}
              onLoadMaxTempValue={setNorthMaxTempValue}
            />
          ) : (
            <NoDataAvailable />
          )}
        </TypeComponentCard>
      </div>

      <div className={styles.scaleContainer}>
        <IRColorScale />
      </div>
    </div>
  );
};

export default IRScanAnalysis;
